import axios from 'axios'
import type { ApiError } from '@/types'

export interface ParsedSyncError {
  /** Mensaje listo para mostrar al usuario final. Sin detalle técnico. */
  userMessage: string
  /** HTTP status recibido del servidor, si aplica. */
  status?: number
  /** Errores por campo tal como los devuelve el backend Laravel (422). */
  fieldErrors?: Record<string, string[]>
  /** Contexto estructurado para soporte/observabilidad. NO se muestra al usuario. */
  technicalDetail: string
}

// Diccionario de campos planos → etiqueta legible en español.
const FIELD_LABELS: Record<string, string> = {
  pickup_id: 'ID del retiro',
  started_at: 'Hora de inicio',
  finished_at: 'Hora de término',
  notes: 'Observaciones',
  items: 'Ítems',
  waste_type_id: 'Tipo de residuo',
  kilos: 'Kilos',
  segregation_status: 'Calidad de segregación',
}

/**
 * Convierte una clave de campo Laravel (incluidas claves anidadas de ítems)
 * en una etiqueta legible en español.
 *
 * Ejemplos:
 *   "items.0.kilos"           → "Ítem 1 · Kilos"
 *   "items.2.segregation_status" → "Ítem 3 · Calidad de segregación"
 *   "finished_at"             → "Hora de término"
 *   "unknown_field"           → "unknown_field"  (fallback)
 */
export function fieldLabel(key: string): string {
  const itemMatch = key.match(/^items\.(\d+)\.(\w+)$/)
  if (itemMatch) {
    const itemIndex = parseInt(itemMatch[1], 10) + 1
    const fieldKey = itemMatch[2]
    const label = FIELD_LABELS[fieldKey] ?? fieldKey
    return `Ítem ${itemIndex} · ${label}`
  }
  return FIELD_LABELS[key] ?? key
}

/**
 * Parsea cualquier error capturado en un flujo de sincronización/guardado
 * y devuelve un `ParsedSyncError` con:
 *  - `userMessage`: texto apto para el usuario final (detalle de validación si es 422)
 *  - `technicalDetail`: payload estructurado para soporte/Sentry (separado del mensaje)
 *
 * @param err - El error capturado (AxiosError, Error local u otro)
 * @param fallbackMessage - Texto genérico cuando el error no es 422 ni validación local.
 *   Úsalo para adaptar el mensaje al contexto (sync vs. guardado directo).
 */
export function parseSyncError(
  err: unknown,
  fallbackMessage = 'No se pudo sincronizar. Inténtalo nuevamente.'
): ParsedSyncError {
  // AxiosError: siempre se trata primero para separar red vs. respuesta del servidor.
  if (axios.isAxiosError(err)) {
    if (!err.response) {
      // Error de red: sin respuesta del servidor (timeout, sin conexión, etc.)
      return {
        userMessage: fallbackMessage,
        technicalDetail: 'network-error',
      }
    }

    const status = err.response.status
    const data = err.response.data as ApiError | undefined

    if (status === 422) {
      const fieldErrors = data?.errors
      const serverMessage = data?.message ?? 'Datos inválidos.'

      let userMessage: string

      if (fieldErrors && Object.keys(fieldErrors).length > 0) {
        // Traduce cada clave y concatena los mensajes de validación.
        const parts = Object.entries(fieldErrors).map(([key, msgs]) => {
          return `${fieldLabel(key)}: ${msgs.join(' ')}`
        })
        userMessage = parts.join(' — ')
      } else {
        userMessage = serverMessage
      }

      const technicalDetail = JSON.stringify({
        status,
        errors: fieldErrors ?? null,
        message: serverMessage,
      })

      return { userMessage, status, fieldErrors: fieldErrors ?? undefined, technicalDetail }
    }

    // Otros errores HTTP (5xx, 4xx distintos de 422)
    const technicalDetail = JSON.stringify({ status, message: data?.message })
    return {
      userMessage: fallbackMessage,
      status,
      technicalDetail,
    }
  }

  // Error de validación local lanzado ANTES de llegar a la API
  // (ej: el draft no tiene finished_at, o falta calidad de segregación).
  if (err instanceof Error) {
    return {
      userMessage: err.message,
      technicalDetail: `local-validation: ${err.message}`,
    }
  }

  // Error desconocido (no Error, no AxiosError)
  return {
    userMessage: fallbackMessage,
    technicalDetail: 'unknown-error',
  }
}
